import React from "react";
import TeamMemberProfileHeader from "./TeamMemberProfileHeader";
import { buildApiUrl } from "../utils/apiConfig";

export type AthleteTeamLeadStaffMember = {
    userId?: number;
    firstName?: string;
    lastName?: string;
    displayName?: string;
    email?: string;
    telephone?: string;
    title?: string;
    role?: string;
    profileImageUrl?: string;
};

type Props = {
    /** Admin/staff/Polaris viewing an athlete — may assign/change Team Lead. */
    canEditTeamLead: boolean;
    canFetchAdminProfile: boolean;
    /** User id of assigned lead (for admin profile fetch). */
    staffMemberUserId?: number | string | null;
    staffMember?: AthleteTeamLeadStaffMember | null;
    refreshTrigger?: number;
    onEditAthlete: () => void;
};

/**
 * Team Lead card / empty banner on the student profile.
 * Edit athlete CTA is admin/staff/Polaris only; athletes/guardians never see it.
 */
export default function AthleteTeamLeadSection({
    canEditTeamLead,
    canFetchAdminProfile,
    staffMemberUserId,
    staffMember,
    refreshTrigger,
    onEditAthlete,
}: Props) {
    const hasAssignedLead = Boolean(staffMemberUserId) || Boolean(staffMember);

    if (!hasAssignedLead) {
        if (!canEditTeamLead) {
            return null;
        }
        return (
            <section
                className="athlete-team-lead-section athlete-team-lead-section--empty"
                data-testid="athlete-team-lead-empty"
                aria-label="Team Lead"
            >
                <p className="athlete-team-lead-empty-message">
                    This student does not currently have a Team Lead assigned. To assign, select
                    &apos;Edit Athlete&apos; and proceed.
                </p>
                <button
                    type="button"
                    className="athlete-team-lead-edit-btn"
                    onClick={onEditAthlete}
                    data-testid="edit-athlete-from-team-lead"
                >
                    Edit athlete
                </button>
            </section>
        );
    }

    return (
        <section
            className="athlete-team-lead-section"
            data-testid="athlete-team-lead-assigned"
            aria-label="Team Lead"
        >
            <div className="athlete-team-lead-card-wrap">
                {canFetchAdminProfile && staffMemberUserId ? (
                    <TeamMemberProfileHeader
                        loadUrl={buildApiUrl(`api/admins/${staffMemberUserId}/profile`)}
                        userId={staffMemberUserId}
                        className="athlete-team-member-header"
                        showEditButton={false}
                        refreshTrigger={refreshTrigger}
                        badgeLabelOverride="LEAD"
                        badgeClassName="tm-profile-badge-lead"
                    />
                ) : staffMember ? (
                    <TeamMemberProfileHeader
                        data={{
                            firstName: staffMember.firstName,
                            lastName: staffMember.lastName,
                            displayName: staffMember.displayName,
                            email: staffMember.email,
                            telephone: staffMember.telephone,
                            title: staffMember.title,
                            role: staffMember.role,
                            avatarUrl: staffMember.profileImageUrl,
                        }}
                        userId={staffMember.userId ?? ""}
                        className="athlete-team-member-header"
                        showEditButton={false}
                        badgeLabelOverride="LEAD"
                        badgeClassName="tm-profile-badge-lead"
                    />
                ) : null}
                {canEditTeamLead && (
                    <button
                        type="button"
                        className="athlete-team-lead-edit-btn athlete-team-lead-edit-btn--on-card"
                        onClick={onEditAthlete}
                        data-testid="edit-athlete-from-team-lead"
                    >
                        Edit athlete
                    </button>
                )}
            </div>
        </section>
    );
}
